/**
 * Scroll utilities for checklist operations.
 *
 * When items are added/removed/converted the task-detail card grows or
 * shrinks.  The focused element can easily slip below the fold.  These
 * helpers use `enhancedSmoothScroll` (block:"nearest") to bring them
 * back into view with the same easing used for card open/close.
 */

import { enhancedSmoothScroll } from "@/shared/utils/ui/enhancedSmoothScroll"
import { getBottomInset } from "@/shared/utils/ui/bottomInset"

/** Duration for micro-scrolls inside the detail card (ms). */
const MICRO_SCROLL_DURATION = 300

/** Find the nearest scrollable ancestor: main scroll area or any overflow container. */
function findScrollContainer(el: HTMLElement): HTMLElement | null {
  return (
    el.closest<HTMLElement>("main.overflow-y-auto") ??
    el.closest<HTMLElement>(".overflow-y-auto")
  )
}

/**
 * Scroll a checklist row (identified by `data-checklist-id`) into view.
 *
 * Waits two rAF frames so React can commit the new DOM before measuring.
 *
 * Does nothing when the row is already comfortably visible — which is the
 * common case while typing a list, so Enter normally causes no scroll at all.
 * When the row HAS slipped out of view it moves the minimum distance.
 *
 * The previous version had a "typewriter mode" that re-centred the row
 * (`block: "center"`) whenever it entered the bottom 25% of the container.
 * Centring is a large jump, and it re-triggered as each new row was added, so
 * the card visibly bounced up and then down again on every Enter. It also
 * computed a 65%-height target that was never used.
 */
export function scrollChecklistRowIntoView(itemId: string): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(
        `[data-checklist-id="${CSS.escape(itemId)}"]`,
      )
      if (!row) return
      const container = findScrollContainer(row)
      if (!container) return

      const rowRect = row.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      // Keep the row off the very edges so the cursor never hugs the fold,
      // and clear of whatever covers the bottom (action panel).
      const BOTTOM_MARGIN = getBottomInset(container, 24)
      const TOP_MARGIN = 8

      const alreadyVisible =
        rowRect.top >= containerRect.top + TOP_MARGIN &&
        rowRect.bottom <= containerRect.bottom - BOTTOM_MARGIN

      if (alreadyVisible) return

      // block:"nearest" moves the minimum distance and never re-centres, so a
      // row that slipped just below the fold scrolls down slightly instead of
      // jumping to the middle.
      enhancedSmoothScroll(row, {
        container,
        duration: MICRO_SCROLL_DURATION,
        block: "nearest",
        bottomInset: BOTTOM_MARGIN,
      })
    })
  })
}

/**
 * Scroll an arbitrary element into view inside the main scroll area.
 *
 * Used for tags, date pickers, and other detail-card sections that
 * appear dynamically and may be off-screen.
 */
export function scrollElementIntoView(el: HTMLElement | null): void {
  if (!el) return
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const container = findScrollContainer(el)
      if (!container) return

      enhancedSmoothScroll(el, {
        container,
        duration: MICRO_SCROLL_DURATION,
        block: "nearest",
        bottomInset: getBottomInset(container, 20),
      })
    })
  })
}
